/**
 * Customer Settings V2 — "Reseller Management" section: native V2
 * rebuild of the legacy `ResellerManagement` tab's dual-list
 * reseller/client relationship editor.
 *
 * Contains:
 * - ResellerManagementSection (default export, forwardRef)
 */

import BusinessIcon from '@mui/icons-material/Business';
import { Alert, Box, Checkbox } from '@mui/material';
import { useFormikContext } from 'formik';
import { Set as ImmutableSet } from 'immutable';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import MultiSelect from '@sophocles/components/MultiSelect';
// eslint-disable-next-line no-restricted-imports -- needs a future useCommandConfirm (dual-direction grant/revoke), not useDeleteConfirm
import WarningDialog from '@sophocles/components/WarningDialog';
import {
  fetchCustomerResellers,
  fetchListOfAllPayingCustomers,
  fetchResellerClients,
  fetchResellers,
} from '@sophocles/entities/Customer';
import { patchCustomerOption } from '@sophocles/entities/CustomerOption';
import { usePutCustomerClientsMutation, usePutCustomerResellersMutation } from '@sophocles/features/entities/customers';
import { SettingsFieldRow, SettingsSection } from '@sophocles/views/settings/components';

import { staffOnlyChip } from './chips';

// Native V2 rebuild of the legacy ``ResellerManagement`` tab.
//
// Same fix pattern as ``ThirdPartySection``: V1 wrapped its body in
// its own ``ContentSection`` chrome, which doubles up when mounted
// inside a V2 ``SettingsSection``.  Inline the state/handlers so the
// SettingsSection is the only chrome and the MultiSelect panes
// stretch full-width.
//
// The section carries no local Save button.  Dirtiness is signalled
// up to the page-level Formik via the `__pending_reseller_dirty`
// synthetic field, and the unified Save bar drives submission through
// the imperative `submit()` handle (CustomerSettingsViewV2.handleSave)
// when the section is dirty and the user holds the assign/unassign
// perms.  The client/reseller PUT hits its own endpoint, not the
// sectioned settings PATCH, so it is folded in as a side dispatch.
// The "Customer is a reseller" direction toggle stays an immediate,
// confirmation-gated action (it flips the list's meaning and refetches
// the candidate pool), separate from the unified Save bar.
//
// Layout shift vs. V1: the "Customer is a reseller" checkbox sits in
// a dedicated ``SettingsFieldRow`` above the two panes (was crammed
// into the V1 ContentSection header alongside the title); the
// MultiSelect spans the full row width like ``ThirdPartySection``.
//
// Same Immutable-via-MultiSelect dependency as ThirdPartySection —
// see the note above that component for why.
const ResellerManagementSection = React.forwardRef(function ResellerManagementSection({ customer, onTablet }, ref) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setFieldValue } = useFormikContext();
  const [putCustomerClients] = usePutCustomerClientsMutation();
  const [putCustomerResellers] = usePutCustomerResellersMutation();
  const { permissions } = useUser();

  const customerId = customer.get('id', 0);
  const initialIsReseller = customer.getIn(['options', 'is_reseller']);

  const [isReseller, setIsReseller] = React.useState(Boolean(initialIsReseller));
  const [selected, setSelected] = React.useState(ImmutableSet());
  const [initial, setInitial] = React.useState(ImmutableSet());
  const [candidates, setCandidates] = React.useState(ImmutableSet());
  const [loading, setLoading] = React.useState(true);
  const [warningOpen, setWarningOpen] = React.useState(false);

  // Fetch both halves: the assigned list (selected) and the
  // candidate pool (unselected).  Pool depends on direction — when
  // this customer is a reseller we list all paying customers as
  // potential clients; otherwise we list all known resellers.
  const fetchAllData = React.useCallback(
    resellerStatus => {
      setLoading(true);
      const poolThunk = resellerStatus ? dispatch(fetchListOfAllPayingCustomers()) : dispatch(fetchResellers());
      poolThunk.then(res => setCandidates(res));
      const assignedThunk = resellerStatus
        ? dispatch(fetchResellerClients(customerId))
        : dispatch(fetchCustomerResellers(customerId));
      assignedThunk.then(res => {
        const assigned = ImmutableSet(res);
        setSelected(assigned);
        setInitial(assigned.map(item => item.get('id')));
        setIsReseller(resellerStatus);
        setLoading(false);
        setFieldValue('__pending_reseller_dirty', false, false);
      });
    },
    [dispatch, customerId, setFieldValue],
  );

  React.useEffect(() => {
    fetchAllData(Boolean(initialIsReseller));
    // Refetch when customer changes; initialIsReseller is derived
    // from the same record so it doesn't need a separate dep.
  }, [fetchAllData, initialIsReseller]);

  // Permissions — checked at the action surface so the disabled
  // affordances are honest about what's possible.  The header
  // "Customer is a reseller" toggle requires change_resellercustomer;
  // add/remove require their respective perms.
  const canAdd = permissions.canAssignResellerCustomers;
  const canRemove = permissions.canUnassignResellerCustomers;
  const canToggleDirection = permissions.canChangeResellerDirection;
  const someFeaturesUnavailable = !canAdd || !canRemove || !canToggleDirection;
  // The unified Save bar dispatches the reseller PUT only when the user
  // holds BOTH assign and unassign; an asymmetric-perm selection can
  // never be saved, so it must never be allowed to dirty the form.
  const canManageResellers = canAdd && canRemove;

  // Dirtiness lives in the page-level Formik so the unified Save bar
  // reflects this section — push a boolean whenever the selection
  // diverges from what loaded.
  const markDirty = next =>
    setFieldValue('__pending_reseller_dirty', !next.map(item => item.get('id')).equals(initial), false);
  const handleSelect = item => {
    if (!canManageResellers) return;
    const next = selected.add(item);
    setSelected(next);
    markDirty(next);
  };
  const handleUnSelect = item => {
    if (!canManageResellers) return;
    const next = selected.remove(item);
    setSelected(next);
    markDirty(next);
  };

  const handleToggleDirection = () => {
    if (!canToggleDirection) return;
    setWarningOpen(true);
  };

  // Imperative submit fired by the page-level Save bar and by the
  // direction-change flow.  Preserves the V1 client/reseller PUT
  // payload byte-for-byte; throws on error so the unified save
  // surfaces it and leaves the form dirty for retry.
  const submit = React.useCallback(async () => {
    const finalIds = selected.map(value => value.get('id')).toArray();
    const result = isReseller
      ? await putCustomerClients({ customerId, body: finalIds })
      : await putCustomerResellers({ customerId, body: finalIds });
    if (result.error) {
      throw new Error(result.error?.data?.detail || '');
    }
    setInitial(ImmutableSet(finalIds));
    setFieldValue('__pending_reseller_dirty', false, false);
  }, [selected, isReseller, putCustomerClients, putCustomerResellers, customerId, setFieldValue]);

  React.useImperativeHandle(ref, () => ({ submit }), [submit]);

  const confirmDirectionChange = () => {
    const optionsId = customer.getIn(['options', 'id']);
    submit().catch(() => {});
    dispatch(patchCustomerOption(optionsId, { is_reseller: !isReseller })).then(() => fetchAllData(!isReseller));
    setWarningOpen(false);
  };

  const selectedIds = selected.map(item => item.get('id'));
  const sortedCandidates = candidates.sortBy(item => item.get('name')).filter(item => item.get('id') !== customerId);
  const unSelectedData = sortedCandidates.filterNot(item => selectedIds.includes(item.get('id')));
  const selectedData = selected.toList();
  const directionLocked = selected.size !== 0;

  const rowGenerator = datum => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
      }}
      title={`${datum.get('id', 0)}. ${datum.get('name', 'none?')}`}
    >
      <Box component="span" sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {datum.get('id', 0)}. {datum.get('name', 'none?')}
      </Box>
    </Box>
  );

  return (
    <SettingsSection
      id="settings-reseller-management"
      icon={<BusinessIcon />}
      title={t('customer.resellerManagement', 'Reseller Management')}
      description={t('customerSettings.resellerDescription', 'Manage reseller customer relationships.')}
      chip={staffOnlyChip(t)}
    >
      {loading ? <LoadingScreen /> : null}

      <SettingsFieldRow
        label={t('reseller.customerReseller', 'Customer is a reseller')}
        description={
          directionLocked
            ? isReseller
              ? t('reseller.cannotChangeClientsAssigned', 'Remove all assigned clients before changing this.')
              : t('reseller.cannotChangeResellersAssigned', 'Remove all assigned resellers before changing this.')
            : t(
                'customerSettings.v2.resellerToggleDescription',
                'Toggle to manage this customer as a reseller of clients instead of a client of resellers.',
              )
        }
        controlWidth="auto"
      >
        <Checkbox
          checked={isReseller}
          onChange={handleToggleDirection}
          disabled={directionLocked || !canToggleDirection}
        />
      </SettingsFieldRow>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        {someFeaturesUnavailable ? <Alert severity="info">{t('reseller.someFeaturesUnavailable')}</Alert> : null}

        <MultiSelect
          selectedData={selectedData}
          unSelectedData={unSelectedData}
          onSelectData={handleSelect}
          onUnSelectData={handleUnSelect}
          selectedTitle={isReseller ? t('reseller.clients') : t('reseller.assignedResellers')}
          unSelectedTitle={isReseller ? t('reseller.allCustomers') : t('reseller.allResellers')}
          rowGenerator={rowGenerator}
          onTablet={onTablet}
        />
      </Box>

      <WarningDialog
        open={warningOpen}
        title={t('general.areYouSure')}
        body={
          <Box>
            {isReseller
              ? t('reseller.revokeResellerConfirm', { name: customer.get('name') })
              : t('reseller.makeResellerConfirm', { name: customer.get('name') })}
          </Box>
        }
        onConfirm={confirmDirectionChange}
        onCancel={() => setWarningOpen(false)}
      />
    </SettingsSection>
  );
});

export default ResellerManagementSection;
